import React from 'react';
import type { LandingCommunicationContent } from '../content/types';
import type { LandingBlockTone } from './LandingPitchBlock';

export type LandingExampleTabsProps = {
  examples: LandingCommunicationContent['examples'];
  tone?: LandingBlockTone;
  className?: string;
  defaultKey?: string;
};

const toneStyles: Record<
  LandingBlockTone,
  {
    card: string;
    title: string;
    body: string;
    tabActive: string;
    tabInactive: string;
    tablist: string;
  }
> = {
  dark: {
    card: 'border-white/10 bg-white/[0.03]',
    title: 'text-white',
    body: 'text-white/80',
    tabActive: 'bg-white text-slate-950 shadow-sm',
    tabInactive: 'text-white/70 hover:text-white',
    tablist: 'border-white/10 bg-white/[0.04]',
  },
  light: {
    card: 'border-slate-200 bg-white',
    title: 'text-slate-950',
    body: 'text-slate-700',
    tabActive: 'bg-slate-900 text-white shadow-sm',
    tabInactive: 'text-slate-600 hover:text-slate-900',
    tablist: 'border-slate-200 bg-slate-50',
  },
  accent: {
    card: 'border-white/20 bg-white/10',
    title: 'text-white',
    body: 'text-white/90',
    tabActive: 'bg-white text-slate-950 shadow-sm',
    tabInactive: 'text-white/80 hover:text-white',
    tablist: 'border-white/20 bg-white/10',
  },
};

function ExampleCard({
  example,
  tone,
}: {
  example: NonNullable<LandingCommunicationContent['examples']>[number];
  tone: LandingBlockTone;
}) {
  const s = toneStyles[tone];
  return (
    <div
      data-edit-tag={example.editTag}
      className={`rounded-2xl border p-6 md:p-8 ${s.card}`}
    >
      {example.title ? (
        <h4 className={`text-lg font-semibold md:text-xl ${s.title}`}>
          {example.title}
        </h4>
      ) : null}
      <p
        className={`${example.title ? 'mt-3' : ''} text-base leading-7 md:text-lg md:leading-8 ${s.body}`}
      >
        {example.body}
      </p>
    </div>
  );
}

/**
 * LandingExampleTabs — 1–4 example tabs.
 * Self-contained state; no router, no URL sync. SSR safe.
 */
export default function LandingExampleTabs({
  examples,
  tone = 'dark',
  className = '',
  defaultKey,
}: LandingExampleTabsProps) {
  if (!examples || examples.length === 0) return null;

  if (examples.length === 1) {
    return (
      <div className={`mx-auto max-w-3xl ${className}`.trim()}>
        <ExampleCard example={examples[0]} tone={tone} />
      </div>
    );
  }

  const initialKey =
    defaultKey && examples.some((e) => e.key === defaultKey)
      ? defaultKey
      : examples[0].key;
  const [activeKey, setActiveKey] = React.useState<string>(initialKey);
  const active = examples.find((e) => e.key === activeKey) ?? examples[0];
  const s = toneStyles[tone];

  return (
    <div className={`mx-auto max-w-3xl ${className}`.trim()}>
      <div
        role="tablist"
        aria-label="Examples"
        className={`mb-6 inline-flex flex-wrap gap-1 rounded-full border p-1 ${s.tablist}`}
      >
        {examples.map((ex) => {
          const isActive = ex.key === active.key;
          return (
            <button
              key={ex.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveKey(ex.key)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                isActive ? s.tabActive : s.tabInactive
              }`}
            >
              {ex.label}
            </button>
          );
        })}
      </div>
      <div role="tabpanel">
        <ExampleCard example={active} tone={tone} />
      </div>
    </div>
  );
}